import { useCallback, Fragment } from "react";
import useModalEvent from "../../hooks/useModalEvent";
import { useTranslation } from "react-i18next";
import {
  Tooltip,
  Avatar,
  AvatarGroup,
  DropDownMenu,
  InfoBar,
  IconDropDownButton
} from "@etframework/ui.components";
import { useBoardProvider } from "../../contexts/board/boardContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useBoardPermissions } from "../../data/queries/boardQueries";
import useEvent from "../../hooks/useEvent";
import useRolePermissionStore from "../../store/rolePermissionStore";
import { useQueryClient } from "@tanstack/react-query";
import viewService from "../../data/api/viewService";
import { useDebounce } from "../../hooks/useDebounce";
import { useHasPendingBoardPermissionRequests } from "../../data/queries/boardPermissionRequestQueries";

const DueDatesTitleRow = ({ appName }) => {
  const rolePermissions = useRolePermissionStore(
    (state) => state.rolePermissions
  );
  const canAccessSettings = rolePermissions?.CanAccessSettings;

  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const { ModalEvents, toggleModal } = useModalEvent();
  const { Events, toggleEvent } = useEvent();
  const { unarchivedBoards, currentBoardId, currentBoard } = useBoardProvider();

  const { data: boardPermissions } = useBoardPermissions(currentBoardId);
  const { data: hasPendingPermissions } =
    useHasPendingBoardPermissionRequests(canAccessSettings);

  const boardFilterValues = unarchivedBoards.map((board) => ({
    id: board?.Id,
    text: board?.Name,
    onClick: () => {
      updateQueryStringParameter("boardId", board?.Id);
    }
  }));

  const { t } = useTranslation(["Layout"], {
    useSuspense: false
  });

  const { t: requestsT } = useTranslation(["ManageBoardPermissionRequests"], {
    useSuspense: false
  });

  // NOTE(ivelez): By prefetching we prevent the <IconLoading /> from blocking
  // all the UI when the user navigates to a different board.
  const prefetchViews = useCallback(
    (boardId) => {
      const { getViews } = viewService();

      queryClient.prefetchQuery({
        queryKey: ["get-views", boardId],
        queryFn: async () => {
          const res = await getViews(boardId);
          return res.data;
        },
        enabled: !!boardId
      });
    },
    [queryClient]
  );

  const moreActionOptions = [
    {
      id: "dd_title-row_more-actions-dropdown-menu_rollover-tasks-button",
      iconCss: "fa-regular fa-arrow-rotate-right more-actions-menu-icon",
      text: t("TitleRow.RolloverTasks"),
      onClick: () => {
        toggleEvent(ModalEvents.RolloverTasks);
      }
    },
    {
      id: "dd_title-row_more-actions-dropdown-menu_export-to-excel-button",
      iconCss: "fa-regular fa-upload more-actions-menu-icon",
      text: t("TitleRow.ExportTasks"),
      onClick: () => {
        toggleEvent(Events.ExportTaskGridToExcel);
      }
    },
    {
      id: "dd_title-row_more-actions-dropdown-menu_import-tasks-button",
      iconCss: "fa-regular fa-download more-actions-menu-icon",
      text: t("TitleRow.ImportTasks"),
      onClick: () => {
        toggleModal(ModalEvents.ImportTasks);
      }
    },
    {
      id: "dd_title-row_more-actions-dropdown-menu_view-import-logs-button",
      iconCss: "fa-regular fa-clipboard-list-check more-actions-menu-icon",
      text: t("TitleRow.ViewImportLog"),
      onClick: () => {
        toggleEvent(Events.ImportLogOverlay);
      }
    }
  ];

  if (import.meta.env.PUBLIC_BULK_UPDATE_ENABLED == "true") {
    moreActionOptions.push({
      id: "dd_title-row_more-actions-dropdown-menu_bulk-update-tasks-button",
      iconCss: "fa-regular fa-pencil more-actions-menu-icon",
      text: t("TitleRow.BulkUpdateTasks"),
      onClick: () => {
        toggleModal(ModalEvents.BulkUpdateTaskModalFromActions);
      }
    });
  }

  const handleCreateTask = () => {
    toggleModal(ModalEvents.CreateTaskModal);
  };

  const handleSelectMoreActions = (args) => {
    args?.onClick?.();
  };

  const handleSelectMenuItem = (args) => {
    args?.item?.onClick?.();
  };

  const onClickOpenAccessModal = () => {
    toggleModal(ModalEvents.AccessModal, {
      currentBoard: currentBoard
    });
  };

  const updateQueryStringParameter = (key, value) => {
    const params = new URLSearchParams(location.search);
    params.set(key, value);
    navigate(`/duedates/views/assignedtome/?${params.toString()}`);
  };

  const debouncedPrefetchViews = useDebounce(prefetchViews, 200);

  const handleMouseEnter = (e) => {
    const boardId = e.currentTarget.getAttribute("id");

    const isInCache = queryClient.getQueryData(["get-views", boardId]);

    if (boardId && !isInCache) {
      debouncedPrefetchViews(boardId);
    }
  };

  const onBoardListOpen = (args) => {
    const liOptions = Array.from(args.element.querySelectorAll("li.e-item"));

    liOptions.forEach((li) => {
      li.addEventListener("mouseenter", handleMouseEnter);
    });

    return () => {
      liOptions.forEach((li) => {
        li.removeEventListener("mouseenter", handleMouseEnter);
      });
    };
  };

  if (!currentBoardId) return null;

  return (
    <Fragment>
      {hasPendingPermissions && (
        <div className="pe-0">
          <InfoBar
            variant="warning"
            dismissable
            title={requestsT("Alert.Title")}
            message={requestsT("Alert.Message")}
            actions={[
              {
                label: requestsT("Alert.Action"),
                onClick: () => {
                  navigate("/manage/board-permission-requests");
                }
              }
            ]}
          />
        </div>
      )}
      <div className="header-title my-3 fs-16">
        <div className="d-flex justify-content-between align-items-center w-100 mx-3">
          <div className="d-flex align-items-center">
            <span className="fw-700 me-2">{appName}</span>
            <i className="title-separator me-2" />
            <div className="d-flex align-items-center">
              <div className="me-2">{t("TitleRow.TasksFor")}</div>
              {unarchivedBoards?.length === 1 ? (
                <div className="fw-700">{unarchivedBoards[0]?.Name}</div>
              ) : (
                <div className="dashboard-main-filter">
                  <span className="fw-bold">{currentBoard.Name}</span>
                  <IconDropDownButton
                    id="dd_title-row_select-board-dropdown"
                    open={onBoardListOpen}
                    className="dashboard-filter-dropdown"
                    isTerciary={true}
                    select={handleSelectMenuItem}
                    items={boardFilterValues}
                    iconCss="fa-solid fa-caret-down"
                  />
                </div>
              )}
            </div>
            <i className="title-separator me-2" />
          </div>
        </div>
        <div className="d-flex gap-2 align-items-center justify-content-end w-100">
          <div className="d-flex align-items-center me-1">
            {rolePermissions?.CanAccessSettings && boardPermissions && (
              <Tooltip
                className={"d-inline"}
                content={() => {
                  return (
                    <p className="p-1 rounded">
                      {t("TitleRow.TooltipBoardCollaborators", {
                        BoardName: currentBoard?.Name
                      })}
                    </p>
                  );
                }}
                position="TopCenter"
                showTipPointer={false}
              >
                <AvatarGroup
                  id="dd_title-row_group-permissions-avatar-group-button"
                  total={boardPermissions?.Entities?.length ?? 0}
                  onClick={onClickOpenAccessModal}
                  max={3}
                >
                  {boardPermissions?.Entities.map((user, index) => (
                    <Avatar
                      key={index}
                      content={user.FullName ?? user.Name}
                    ></Avatar>
                  ))}
                </AvatarGroup>
              </Tooltip>
            )}
          </div>
          <div className="">
            <Tooltip
              className={"d-inline"}
              content={() => {
                return (
                  <p className="p-1 rounded">
                    {t("TitleRow.TooltipMoreActions")}
                  </p>
                );
              }}
              dark={true}
              position="TopCenter"
              showTipPointer={false}
            >
              <DropDownMenu
                id="dd_title-row_more-actions-dropdown-menu"
                className="btn"
                items={moreActionOptions}
                select={handleSelectMoreActions}
              >
                {t("TitleRow.MoreActions")}
              </DropDownMenu>
            </Tooltip>
          </div>
          <div>
            <button
              id="dd_title-row_create-task-button"
              onClick={handleCreateTask}
              className="btn btn-primary"
            >
              <i className="far fa-plus pe-2"></i> {t("TitleRow.CreateTask")}
            </button>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default DueDatesTitleRow;
